import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface YouTubeModalProps {
  videoId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const YouTubeModal: React.FC<YouTubeModalProps> = ({
  videoId,
  isOpen,
  onClose,
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [scrollYBeforeOpen, setScrollYBeforeOpen] = useState<number | null>(
    null
  );
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  // Create portal container on mount
  useEffect(() => {
    const container = document.createElement('div');
    container.id = 'youtube-modal-portal';
    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      document.body.removeChild(container);
    };
  }, []);

  // Reset states when modal opens/closes or video changes
  useEffect(() => {
    if (isOpen && videoId) {
      setIframeLoaded(false);
      setHasError(false);
    }
  }, [isOpen, videoId]);

  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      // Remember current scroll position so we can restore it after close
      const currentScrollY = window.scrollY || window.pageYOffset;
      setScrollYBeforeOpen(currentScrollY);

      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      // Restore scroll position after the browser reflows
      if (scrollYBeforeOpen !== null) {
        window.scrollTo({ top: scrollYBeforeOpen });
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      if (scrollYBeforeOpen !== null) {
        window.scrollTo({ top: scrollYBeforeOpen });
      }
    };
  }, [isOpen, onClose, scrollYBeforeOpen]);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIframeLoaded(false);
    setHasError(true);
  };

  // Create the embed URL
  const getEmbedUrl = (videoId: string): string => {
    const params = new URLSearchParams({
      autoplay: '1',
      rel: '0',
      modestbranding: '1',
      playsinline: '1',
      enablejsapi: '1',
      origin: window.location.origin,
    });
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  };

  if (!isOpen || !videoId || !portalContainer) return null;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      />

      {/* Modal Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 100000,
          width: '100%',
          maxWidth: '1024px',
          maxHeight: '90vh',
          margin: '0 auto',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '-48px',
            right: '0',
            zIndex: 100001,
            padding: '12px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          }}
          aria-label="Close video"
        >
          <XMarkIcon style={{ width: '24px', height: '24px' }} />
        </button>

        {/* Video Container */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%', // 16:9 aspect ratio
            backgroundColor: '#000',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow:
              '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Loading State */}
          {!iframeLoaded && !hasError && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
                color: 'white',
              }}
            >
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4" />
              <div style={{ fontSize: '18px' }}>Loading video...</div>
            </div>
          )}

          {/* Error State */}
          {hasError && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
                color: 'white',
                padding: '20px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>
                Failed to load video
              </div>
              <div
                style={{
                  fontSize: '14px',
                  opacity: 0.75,
                  marginBottom: '24px',
                }}
              >
                The video might be unavailable or restricted
              </div>
              <a
                href={`https://www.youtube.com/watch?v=${videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '14px',
                }}
              >
                Open on YouTube
              </a>
            </div>
          )}

          {/* YouTube Iframe */}
          <iframe
            key={videoId}
            src={getEmbedUrl(videoId)}
            title="YouTube video player"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, portalContainer);
};

export default YouTubeModal;
